import { useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/react/shallow'
import { FORMAT_DD_MM_YYYY } from '../utils/constantes.js'
import dayjs from '../utils/dayjsHelper.js'
import { getMondayOfSameWeek } from '../utils/utils.js'

// Get the current date in the url
const initSearchParams = new URLSearchParams(window.location.search)
const dateParam = initSearchParams.get('date')
const formattedDate = dayjs(dateParam, FORMAT_DD_MM_YYYY)
const initDateDayJs = formattedDate.isValid() ? formattedDate : dayjs()

export const useDateStore = create(
  immer((set) => ({
    date: initDateDayJs,
    monday: getMondayOfSameWeek(initDateDayJs),

    setDate: (nextDate) =>
      set((state) => {
        state.date = nextDate
        state.monday = getMondayOfSameWeek(nextDate)
      }),
  }))
)

const getNextRefreshDate = (refreshHour) => {
  const now = dayjs()
  const nextRefresh = now.hour(refreshHour).minute(0).second(0).millisecond(0)

  return now.isBefore(nextRefresh) ? nextRefresh : nextRefresh.add(1, 'day')
}

export const DateStoreWrapper = ({ children, autoRefreshTodayAtHour = null }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const shouldAutoRefreshToday = useRef(null)

  const { date, setDate } = useDateStore(
    useShallow((state) => ({
      date: state.date,
      setDate: state.setDate,
    }))
  )

  if (shouldAutoRefreshToday.current === null) {
    shouldAutoRefreshToday.current = dayjs(date).isSame(dayjs(), 'day')
  }

  // Reflect the date change into the url
  useEffect(() => {
    const dateInSearchString = searchParams.get('date')
    const currentDate = dayjs(date).format(FORMAT_DD_MM_YYYY)

    if (currentDate !== dateInSearchString) {
      const dayjsDate = dayjs(date)
      if (dayjsDate.isValid()) {
        searchParams.set('date', dayjsDate.format(FORMAT_DD_MM_YYYY))
      } else {
        searchParams.set('date', dayjs().format(FORMAT_DD_MM_YYYY))
      }
      setSearchParams(searchParams, { replace: true })
    }
  }, [date, searchParams, setSearchParams])

  const refreshDateIfNeeded = useCallback(() => {
    if (autoRefreshTodayAtHour === null || !shouldAutoRefreshToday.current) return

    const today = dayjs()
    if (!dayjs(useDateStore.getState().date).isSame(today, 'day')) {
      setDate(today)
    }
  }, [autoRefreshTodayAtHour, setDate])

  useEffect(() => {
    if (autoRefreshTodayAtHour === null || !shouldAutoRefreshToday.current) return

    const refreshHour = Number(autoRefreshTodayAtHour)
    if (!Number.isInteger(refreshHour) || refreshHour < 0 || refreshHour > 23) return

    let timeoutId

    const scheduleNextRefresh = () => {
      const nextRefresh = getNextRefreshDate(refreshHour)
      timeoutId = window.setTimeout(() => {
        refreshDateIfNeeded()
        scheduleNextRefresh()
      }, nextRefresh.diff(dayjs()))
    }

    const refreshOnWake = () => {
      const now = dayjs()
      const currentDate = dayjs(useDateStore.getState().date)
      const refreshDate = currentDate
        .add(1, 'day')
        .hour(refreshHour)
        .minute(0)
        .second(0)
        .millisecond(0)

      if (now.isSame(currentDate, 'day') || now.isBefore(refreshDate)) return
      refreshDateIfNeeded()
    }

    const refreshOnVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshOnWake()
      }
    }

    scheduleNextRefresh()
    window.addEventListener('focus', refreshOnWake)
    document.addEventListener('visibilitychange', refreshOnVisibilityChange)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('focus', refreshOnWake)
      document.removeEventListener('visibilitychange', refreshOnVisibilityChange)
    }
  }, [autoRefreshTodayAtHour, refreshDateIfNeeded])

  return children
}
